import { Controller } from 'react-hook-form';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FeatureType } from '../../../generated/ApiClient';
import { FieldValues } from 'react-hook-form/dist/types';
import { FormComponentProps } from '../../../component/form/FormComponentProps';
import { useFeatureTypes } from '../features/useFeatureTypes';

interface Props<TFieldValues extends FieldValues>
  extends Omit<FormComponentProps<TFieldValues>, 'name'> {
  typeFieldName: string;
  valueFieldName: string;
  readOnly?: boolean;
  disableType?: boolean;
}

export const FeatureValueInput = <TFieldValues extends FieldValues>({
  control,
  label,
  typeFieldName,
  valueFieldName,
  className,
  shrink,
  disabled,
  disableUnderline,
  readOnly,
  disableType,
}: Props<TFieldValues>) => {
  const { t } = useTranslation();

  const { featureTypes } = useFeatureTypes();

  const defaultLabel = t('classification.features.value');

  return (
    <Box className={className}>
      <Controller
        name={typeFieldName as any}
        control={control}
        render={({ field: typeField, fieldState: { error: typeError } }) => (
          <FormControl
            fullWidth
            variant="standard"
            error={!!typeError}
            sx={{ mb: 2 }}
            disabled={disabled}
          >
            <InputLabel shrink={shrink}>{t('classification.features.type')}</InputLabel>
            <Select
              {...typeField}
              value={typeField.value || ''}
              readOnly={readOnly}
              disabled={readOnly || disabled || disableType}
              disableUnderline={disableUnderline}
            >
              {featureTypes.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      />

      <Controller
        name={valueFieldName as any}
        control={control}
        render={({ field: valueField, fieldState: { error: valueError } }) => (
          <Controller
            name={typeFieldName as any}
            control={control}
            render={({ field: typeField }) => {
              const currentType = typeField.value as FeatureType;

              switch (currentType) {
                case FeatureType.Boolean:
                  return (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!valueField.value}
                          onChange={(e) => valueField.onChange(e.target.checked)}
                          disabled={readOnly || disabled}
                        />
                      }
                      label={label || defaultLabel}
                    />
                  );

                case FeatureType.Number:
                  return (
                    <TextField
                      {...valueField}
                      label={label || defaultLabel}
                      type="number"
                      variant="standard"
                      fullWidth
                      error={!!valueError}
                      helperText={valueError?.message}
                      value={valueField.value || ''}
                      InputProps={{ readOnly, disableUnderline }}
                      InputLabelProps={{ shrink }}
                      disabled={readOnly || disabled}
                    />
                  );

                case FeatureType.Date:
                  return (
                    <TextField
                      {...valueField}
                      label={label || defaultLabel}
                      type="date"
                      variant="standard"
                      fullWidth
                      error={!!valueError}
                      helperText={valueError?.message}
                      value={valueField.value || ''}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{ readOnly, disableUnderline }}
                      disabled={readOnly || disabled}
                    />
                  );

                case FeatureType.Text:
                case FeatureType.Reference:
                default:
                  return (
                    <TextField
                      {...valueField}
                      label={label || defaultLabel}
                      type="text"
                      variant="standard"
                      fullWidth
                      error={!!valueError}
                      helperText={valueError?.message}
                      value={valueField.value || ''}
                      InputProps={{ readOnly, disableUnderline }}
                      InputLabelProps={{ shrink }}
                      disabled={readOnly || disabled}
                    />
                  );
              }
            }}
          />
        )}
      />
    </Box>
  );
};
